// src/pages/exercise/components/Scene/Model.jsx
import { useGLTF, useTexture } from '@react-three/drei';
import { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import useWeather from '../../hooks/useWeather';
import { createWeatherCanvas } from '../../utils/weatherCanvas';

function SceneModel() {
  // 1. Load Geometry (Tiny, Geometry-only)
  const gltf = useGLTF('/assets/models/scene/m2m-scene-draco.glb');
  
  // 2. Load Baked Texture (Cached independently)
  const bakedTexture = useTexture('/assets/textures/scene/m2m-scene.webp');

  const { weatherData, loading, error } = useWeather();
  
  // Refs for dynamic cleanup
  const tvTextureRef = useRef(null);
  const windowTextureRef = useRef(null);
  const tvMaterialRef = useRef(null);
  const windowMaterialRef = useRef(null);

  // === STEP A: APPLY BAKED TEXTURE (The "Base Coat") ===
  // We use useLayoutEffect to paint the scene BEFORE it paints to screen (prevents flashing)
  useLayoutEffect(() => {
    // Configure the baked texture
    bakedTexture.flipY = false;
    bakedTexture.colorSpace = THREE.SRGBColorSpace;
    
    // Create the shared material
    const bakedMaterial = new THREE.MeshBasicMaterial({ 
      map: bakedTexture,
      side: THREE.DoubleSide
    });

    // Traverse the scene and apply this material to everything...
    // ...EXCEPT your special screens (TV and Window)
    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        // IGNORE these two meshes so your other useEffect can handle them
        if (
          child.name === 'selectionscene-tv_screen' || 
          child.name === 'selectionscene-windowscreen' ||
          child.name === 'gymscene-gymbikescreen1' ||
          child.name === 'gymscene-gymbikescreen2' ||
          child.name === 'gymscene-treadmillscreen'
        ) {
          return;
        }
        child.material = bakedMaterial;
      }
    });
  }, [gltf.scene, bakedTexture]);


  // === STEP B: HANDLE DYNAMIC SCREENS (Your Existing Logic) ===
  useEffect(() => {
    const tvScreen = gltf.scene.getObjectByName('selectionscene-tv_screen');
    const windowScreen = gltf.scene.getObjectByName('selectionscene-windowscreen');

    if (!tvScreen || !windowScreen) return;

    // --- TV SCREEN LOGIC ---
    if (loading || error) {
      const loader = new THREE.TextureLoader();
      loader.load('/assets/backgrounds/tv-sizzle.jpg', (texture) => {
        if (tvTextureRef.current) tvTextureRef.current.dispose();
        if (tvMaterialRef.current) tvMaterialRef.current.dispose();
        
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.center.set(0.5, 0.5);
        texture.rotation = 0;
        texture.flipY = false; 

        const material = new THREE.MeshBasicMaterial({ map: texture });
        tvScreen.material = material;
        tvTextureRef.current = texture;
        tvMaterialRef.current = material;
      });
    } else if (weatherData) {
        const canvas = createWeatherCanvas(weatherData);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.center.set(0.5, 0.5);
        texture.rotation = 0;
        texture.flipY = false;

        if (tvTextureRef.current) tvTextureRef.current.dispose();
        if (tvMaterialRef.current) tvMaterialRef.current.dispose();
        
        const material = new THREE.MeshBasicMaterial({ map: texture });
        tvScreen.material = material;
        tvTextureRef.current = texture;
        tvMaterialRef.current = material;
    }

    // --- WINDOW SCREEN LOGIC ---
    if (loading || error) {
        const loader = new THREE.TextureLoader();
        loader.load('/assets/backgrounds/cloudy.jpg', (texture) => {
             if (windowTextureRef.current) windowTextureRef.current.dispose();
             if (windowMaterialRef.current) windowMaterialRef.current.dispose();
             texture.colorSpace = THREE.SRGBColorSpace;
             texture.center.set(0.5, 0.5);
             texture.rotation = 0;
             texture.flipY = false;

             const material = new THREE.MeshBasicMaterial({ map: texture });
             windowScreen.material = material;
             windowTextureRef.current = texture;
             windowMaterialRef.current = material;
        });
    } else if (weatherData) {
        const weatherImageMap = {
            'sunny': '/assets/backgrounds/sunny.jpg',
            'cloudy': '/assets/backgrounds/cloudy.jpg',
            'rainy': '/assets/backgrounds/rainy.jpg',
            'snowy': '/assets/backgrounds/snowy.jpg'
        };
        const imagePath = weatherImageMap[weatherData.condition] || weatherImageMap['cloudy'];
        
        const loader = new THREE.TextureLoader();
        loader.load(imagePath, (texture) => {
            if (windowTextureRef.current) windowTextureRef.current.dispose();
            if (windowMaterialRef.current) windowMaterialRef.current.dispose();
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.center.set(0.5, 0.5);
            texture.rotation = 0;
            texture.flipY = false;

            const material = new THREE.MeshBasicMaterial({ map: texture });
            windowScreen.material = material;
            windowTextureRef.current = texture;
            windowMaterialRef.current = material;
        });
    }

    // Cleanup
    return () => {
      tvTextureRef.current?.dispose();
      windowTextureRef.current?.dispose();
      tvMaterialRef.current?.dispose();
      windowMaterialRef.current?.dispose();
    };
  }, [gltf.scene, weatherData, loading, error]);

  return (
    <primitive 
      object={gltf.scene} 
      position={[0, 0, 0]}
      rotation={[0, 0, 0]}
      scale={[1, 1, 1]}
    />
  );
}

// Updated Preload Paths
useGLTF.preload('/assets/models/scene/m2m-scene-draco.glb');
useTexture.preload('/assets/textures/scene/m2m-scene.webp');

export default SceneModel;
